import axios from 'axios';
import type { ApiResponse, LatestDrawResponse, DrawRecord, PlayType, ZiliaoRecord, ZiliaoGroup, GameType, BallInfo, LotteryLegend, DrawTimeConfig, LotteryAdResponse, LotterySiteSettings } from '../types/lottery';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 10000,
});

// 是否使用 mock 数据（后端未启动时自动降级）
const USE_MOCK = false;

// Mock 号码球数据
function mockBall(title: string, cls: 'red' | 'blue' | 'green', sx: string, wx: string): BallInfo {
  return { boll: String(parseInt(title)), class: cls, title, sx: { name: sx, wuxing: wx } };
}

const mockBalls: BallInfo[] = [
  mockBall('08', 'red', '马', '木'),
  mockBall('23', 'red', '兔', '木'),
  mockBall('36', 'blue', '龙', '土'),
  mockBall('11', 'green', '蛇', '火'),
  mockBall('45', 'red', '猴', '金'),
  mockBall('02', 'red', '羊', '土'),
  mockBall('49', 'green', '鼠', '水'),
];

const MOCK_TIMEZONE_BY_GAME: Record<GameType, string> = {
  xglhc: 'Asia/Hong_Kong',
  amlhc: 'Asia/Macau',
  xamlhc: 'Asia/Macau',
  amssc: 'Asia/Macau',
};

const MOCK_DRAW_TIME_BY_GAME: Record<GameType, { draw_time: string; hour: number; minute: number; second: number }> = {
  xglhc: { draw_time: '21:30:00', hour: 21, minute: 30, second: 0 },
  amlhc: { draw_time: '22:30:00', hour: 22, minute: 30, second: 0 },
  xamlhc: { draw_time: '22:30:00', hour: 22, minute: 30, second: 0 },
  amssc: { draw_time: '22:30:00', hour: 22, minute: 30, second: 0 },
};

function mockHistory(game: string, page: number, limit: number): DrawRecord[] {
  const records: DrawRecord[] = [];
  const base = 2026088 - (page - 1) * limit;
  for (let i = 0; i < limit && base - i > 2026000; i++) {
    records.push({
      game,
      period: base - i,
      isopen: 2,
      date: `2026-03-${String(28 - i).padStart(2, '0')}`,
      format: mockBalls.map((b, idx) => ({
        ...b,
        title: String((parseInt(b.title) + i + idx) % 49 + 1).padStart(2, '0'),
      })),
    });
  }
  return records;
}

export async function getLatestDraw(gameid: GameType): Promise<LatestDrawResponse> {
  if (USE_MOCK) {
    return {
      prev: { format: mockBalls, period: 2026088, isopen: 2 },
      next: { period: 2026089, is_open: false, opentime: Math.floor(Date.now() / 1000) + 3600 },
    };
  }
  const res = await api.get<ApiResponse<LatestDrawResponse>>('/lottery/latest', {
    params: { gameid },
  });
  return res.data.data;
}

export async function finalizePendingDraws(game: GameType): Promise<void> {
  if (USE_MOCK) {
    return;
  }
  try {
    await api.get('/lottery/finalize-pending', { params: { game } });
  } catch {
    // ignore
  }
}

export async function getDrawHistory(game: GameType, page = 1, limit = 10): Promise<DrawRecord[]> {
  if (USE_MOCK) {
    return mockHistory(game, page, limit);
  }
  const res = await api.get<ApiResponse<DrawRecord[]>>('/lottery/history', {
    params: { game, page, limit },
  });
  return res.data.data;
}

export async function getDrawTimeConfig(gameid: GameType): Promise<DrawTimeConfig> {
  if (USE_MOCK) {
    return {
      ...MOCK_DRAW_TIME_BY_GAME[gameid],
      timezone: MOCK_TIMEZONE_BY_GAME[gameid],
      game: gameid,
    };
  }
  const res = await api.get<ApiResponse<DrawTimeConfig>>('/lottery/drawtime', {
    params: { gameid },
  });
  return res.data.data;
}

export async function getPlayTypes(game: GameType): Promise<PlayType[]> {
  const res = await api.get<ApiResponse<PlayType[]>>('/lottery/types', {
    params: { game },
  });
  return res.data.data;
}

export async function getLotteryLegend(game: GameType): Promise<LotteryLegend> {
  const res = await api.get<ApiResponse<LotteryLegend>>('/lottery/legend', {
    params: { game },
  });
  return res.data.data;
}

export async function getZiliao(game: GameType, type: string, limit = 10): Promise<ZiliaoRecord[]> {
  const res = await api.get<ApiResponse<ZiliaoRecord[]>>('/lottery/ziliao', {
    params: { game, type, limit },
  });
  return res.data.data;
}

export async function getZiliaoAll(game: GameType, limit = 10): Promise<ZiliaoGroup[]> {
  const res = await api.get<ApiResponse<ZiliaoGroup[]>>('/lottery/ziliaoall', {
    params: { game, limit },
  });
  return res.data.data;
}

export async function getLotteryAds(position: string, limit = 10): Promise<LotteryAdResponse> {
  const res = await api.get<ApiResponse<LotteryAdResponse>>('/lottery/ads', {
    params: { position, limit },
  });
  return res.data.data;
}

export async function getLotterySiteSettings(): Promise<LotterySiteSettings> {
  const res = await api.get<ApiResponse<LotterySiteSettings>>('/lottery/site');
  return res.data.data;
}
